/**
 * RCT design results, laid out like the RCT Experiment's Results screen (ResultsStep.tsx):
 * winning trial detail, market level investments (with .xlsx download), actual vs predicted
 * chart and the investment calculation — each in an expand / collapse section.
 */
import React, { useMemo, useState } from "react";
import LineChartWithTitle from "../../components/LineChartWithTitle";
import { CollapseSection, errMsg } from "./components";
import { RctDesign, ccApi } from "./api";

const currency = (n: number) => `$${Math.round(n).toLocaleString()}`;
const formatNumber = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 2 });
const fmt = (n: number | null | undefined) => (n != null ? formatNumber(n) : "N/A");

interface MarketRow {
  name: string;
  type: "Treatment" | "Control";
  totalSalesCount: number;
  percentageOfTreatment: number; // 0-100; always 0 for Control
}

type SortKey = "name" | "totalSalesCount" | "percentageOfTreatment";
const PAGE = 8;

const th: React.CSSProperties = { textAlign: "left", padding: "8px 10px", fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", borderBottom: "1px solid var(--border-subtle)", whiteSpace: "nowrap" };
const td: React.CSSProperties = { padding: "8px 10px", fontSize: 13, borderBottom: "1px solid var(--border-subtle)" };

const RctDesignResults: React.FC<{
  testId: string;
  jobId?: string;
  design: RctDesign;
  marketSales?: Record<string, number>;
  onError: (msg: string) => void;
}> = ({ testId, jobId, design, marketSales, onError }) => {
  const x = design.exact || {};
  const weeks = design.weeks;
  const invest = x.incrementalInvestment ?? design.investment_k * 1000;

  // ResultsStep buildMarketLevelRows: full-history KPI per market, share of the treatment total.
  const rows = useMemo<MarketRow[]>(() => {
    if (!marketSales) return [];
    const total = design.markets.reduce((s, m) => s + (marketSales[m] || 0), 0);
    return [
      ...design.markets.map(name => ({
        name, type: "Treatment" as const, totalSalesCount: marketSales[name] || 0,
        percentageOfTreatment: total > 0 ? ((marketSales[name] || 0) / total) * 100 : 0,
      })),
      ...design.control.map(name => ({ name, type: "Control" as const, totalSalesCount: marketSales[name] || 0, percentageOfTreatment: 0 })),
    ];
  }, [design, marketSales]);

  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: "name", dir: 1 });
  const [page, setPage] = useState(0);
  const sorted = useMemo(() => [...rows].sort((a, b) => {
    const v = sort.key === "name" ? a.name.localeCompare(b.name) : a[sort.key] - b[sort.key];
    return v * sort.dir;
  }), [rows, sort]);
  const pages = Math.max(1, Math.ceil(sorted.length / PAGE));
  const shown = sorted.slice(page * PAGE, page * PAGE + PAGE);
  const sortBy = (key: SortKey) => { setSort(p => ({ key, dir: p.key === key ? (p.dir === 1 ? -1 : 1) : 1 })); setPage(0); };
  const arrow = (key: SortKey) => (sort.key === key ? (sort.dir === 1 ? " ▲" : " ▼") : "");

  const [downloading, setDownloading] = useState(false);
  const download = async () => {
    setDownloading(true);
    try { await ccApi.downloadRctMarketLevel(testId); } catch (err) { onError(`Could not download the file: ${errMsg(err)}`); } finally { setDownloading(false); }
  };

  const chart = design.chart;
  const k = jobId || "rct";

  return (
    <>
      <CollapseSection key={`trial-${k}`} title="Winning trial detail" hint="Best of the search across your candidate durations" defaultOpen>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>{["Duration", "RMSPE", "Resid. Std Dev", "Resid. Bias", "Normalized Average Error", "Invest / Wk"].map(h => <th key={h} style={th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              <tr>
                <td style={td}>{weeks} wks</td>
                <td style={td} className="cc-mono">{fmt(x.validationRMSPE ?? design.rmspe)}</td>
                <td style={td} className="cc-mono">{fmt(x.validationSD)}</td>
                <td style={td} className="cc-mono">{fmt(x.validationBias)}</td>
                <td style={td} className="cc-mono">{fmt(x.normalizedAverageError)}</td>
                <td style={td} className="cc-mono">{currency(invest / weeks)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <span className="cc-help">
          Picked by fit quality among trials that already met your weekly spend cap. Normalized Average Error combines
          RMSPE + Std Dev + Bias (lower score wins) across every trial tried for this split.
        </span>
      </CollapseSection>

      <CollapseSection key={`markets-${k}`} title="Market level investments" hint={`${rows.length || design.test_markets + design.control_markets} markets · ${weeks} wk duration`}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span className="cc-help">Treatment / control assignment for this split, from the market pairing computed on your uploaded data.</span>
          <button className="cc-btn" disabled={downloading} onClick={download} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M12 3v12m0 0l-5-5m5 5l5-5M4 19h16" /></svg>
            {downloading ? "Downloading…" : "Download"}
          </button>
        </div>
        {!rows.length ? (
          <span className="cc-help">Re-run the RCT design to see the market table here; Download works for this run.</span>
        ) : (
          <>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ ...th, cursor: "pointer" }} onClick={() => sortBy("name")}>Market{arrow("name")}</th>
                    <th style={th}>Type</th>
                    <th style={{ ...th, cursor: "pointer" }} onClick={() => sortBy("totalSalesCount")}>Total Sales Count{arrow("totalSalesCount")}</th>
                    <th style={{ ...th, cursor: "pointer" }} onClick={() => sortBy("percentageOfTreatment")}>Percentage Of Treatment{arrow("percentageOfTreatment")}</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map(r => (
                    <tr key={r.name}>
                      <td style={td}>{r.name}</td>
                      <td style={td}>
                        <span style={{
                          padding: "2px 10px", borderRadius: 999, fontSize: 12, fontWeight: 600,
                          background: r.type === "Treatment" ? "var(--blue-subtle, #DBEAFE)" : "var(--bg-base)",
                          color: r.type === "Treatment" ? "var(--blue, #2563EB)" : "var(--text-secondary)",
                        }}>{r.type}</span>
                      </td>
                      <td style={td} className="cc-mono">{Math.round(r.totalSalesCount).toLocaleString()}</td>
                      <td style={{ ...td, color: r.type === "Treatment" ? undefined : "var(--text-tertiary)" }} className="cc-mono">
                        {r.type === "Treatment" ? `${formatNumber(r.percentageOfTreatment)}%` : "0%"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pages > 1 && (
              <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, fontSize: 13 }}>
                <button className="cc-btn" style={{ padding: "3px 10px" }} disabled={page === 0} onClick={() => setPage(p => p - 1)} aria-label="Previous page">‹</button>
                <span className="cc-help">Page {page + 1} of {pages}</span>
                <button className="cc-btn" style={{ padding: "3px 10px" }} disabled={page >= pages - 1} onClick={() => setPage(p => p + 1)} aria-label="Next page">›</button>
              </div>
            )}
          </>
        )}
      </CollapseSection>

      <CollapseSection key={`chart-${k}`} title="Actual vs. predicted — test market average" hint={`Best trial · ${weeks}-week test`}>
        {chart?.actual?.length ? (
          <LineChartWithTitle
            title=""
            labels={chart.dates}
            xAxisTitle="Week"
            yAxisTitle="Avg KPI"
            height="320px"
            dataSets={[
              { name: "Actual test average", data: chart.actual, borderColor: "#0055B8", backgroundColor: "#0055B8", textColor: "#0055B8" },
              { name: "Predicted (synthetic control)", data: chart.predicted, borderColor: "#9CA3AF", backgroundColor: "#9CA3AF", textColor: "#9CA3AF", lineType: "dashed" },
            ]}
          />
        ) : (
          <span className="cc-help">No post-campaign window data available for this trial.</span>
        )}
      </CollapseSection>

      <CollapseSection key={`invest-${k}`} title="Investment calculation" hint={`Best duration · ${weeks} wks`}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {([
            // The engine returns selectedEffectSize (guessEffectSize - 1); shown as the original guess.
            ["Effect Size", x.selectedEffectSize != null ? formatNumber(x.selectedEffectSize + 1) : "N/A"],
            ["Standard Error", fmt(x.standardError)],
            ["Min. Detectable Effect", fmt(x.mde)],
            ["Percentage Lift", x.percentageLift != null ? `${formatNumber(x.percentageLift)}%` : `${formatNumber(design.mde)}%`],
            ["Average Baseline KPI", fmt(x.averageBaselineKPI)],
            ["Treatment Locations × Duration", String(design.test_markets * weeks)],
            ["Investment Per Week", currency(invest / weeks)],
          ] as [string, string][]).map(([name, value]) => (
            <div key={name} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "10px 0", borderBottom: "1px solid var(--border-subtle)" }}>
              <span style={{ fontWeight: 600, fontSize: 13.5 }}>{name}</span>
              <span className="cc-mono" style={{ fontWeight: 600, color: "var(--blue, #2563EB)" }}>{value}</span>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", borderRadius: 8, background: "var(--blue, #2563EB)", color: "#fff" }}>
          <span style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>Incremental investment (overall)</span>
          <span className="cc-mono" style={{ fontSize: 22, fontWeight: 700 }}>{currency(invest)}</span>
        </div>
      </CollapseSection>
    </>
  );
};

export default RctDesignResults;
